import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useToast } from '../../../hooks/useToast';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '../../common/Card';
import { Button } from '../../common/Button';
import {
  CalendarIcon,
  PlayIcon,
  StopIcon,
  ArrowRightIcon,
  CheckCircledIcon,
  CrossCircledIcon,
  TimerIcon,
} from '@radix-ui/react-icons';
import { startThirtyDaysChallenge, stopThirtyDaysChallenge } from '../../../api/thirty-day-challenge';

const ThirtyDaysChallengeCard = ({ data, activityId }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleStartChallenge = async () => {
    try {
      setLoading(true);
      await startThirtyDaysChallenge(activityId);
      toast({
        title: "Success",
        description: "30 Days Challenge started!",
        type: "success"
      });
      window.location.reload();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to start challenge',
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStopChallenge = async () => {
    if (!window.confirm('Are you sure you want to stop this challenge? Your progress will be saved in history.')) {
      return;
    }
    
    try {
      setLoading(true);
      await stopThirtyDaysChallenge(activityId, data.id);
      toast({
        title: "Success",
        description: "Challenge stopped",
        type: "success"
      });
      window.location.reload();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to stop challenge',
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = () => {
    navigate(`/activity/${activityId}/30-day-challenge/${data.id}`);
  };

  const renderCalendar = () => {
    const days = data.days || [];
    const today = new Date().toISOString().split('T')[0];

    return (
      <div className="grid grid-cols-7 gap-1">
        <AnimatePresence mode="popLayout">
          {days.map((day) => {
            const dayDate = day.date;
            const isPast = dayDate < today;
            const isToday = dayDate === today;
            
            let statusClass = '';
            if (day.completed) {
              statusClass = 'bg-primary/10 text-primary dark:bg-primary/20';
            } else if (isPast) {
              statusClass = 'bg-destructive/10 text-destructive dark:bg-destructive/20';
            } else if (isToday) {
              statusClass = 'bg-secondary/10 text-secondary dark:bg-secondary/20 ring-2 ring-secondary';
            } else {
              statusClass = 'bg-muted/10 text-muted-foreground dark:bg-muted/20';
            }

            return (
              <motion.div
                key={day.dayNumber}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className={`flex h-8 w-8 items-center justify-center rounded-md ${statusClass} font-medium`}
                title={`Day ${day.dayNumber}: ${new Date(dayDate).toLocaleDateString()}`}
              >
                {day.dayNumber}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    );
  };

  const renderChallengeStats = () => {
    if (!data.stats) return null;
    
    const { completedDays, totalDays, completionPercentage } = data.stats;
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TimerIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Progress</span>
          </div>
          <span className="font-medium">
            {completedDays}/{totalDays} days
          </span>
        </div>
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary/20">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${completionPercentage}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="h-full bg-primary"
          />
        </div>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1">
            <CheckCircledIcon className="h-4 w-4 text-primary" />
            <span>{completedDays} completed</span>
          </div>
          <div className="flex items-center gap-1">
            <CrossCircledIcon className="h-4 w-4 text-destructive" />
            <span>{totalDays - completedDays} remaining</span>
          </div>
        </div>
      </div>
    );
  };

  if (!data || data.status === 'NOT_STARTED') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            30 Days Challenge
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <p className="mb-6 text-sm text-muted-foreground">
            Challenge yourself to complete this activity every day for 30 days!
          </p>
          <Button
            onClick={handleStartChallenge}
            disabled={loading}
            className="w-full"
          >
            <PlayIcon className="mr-2 h-4 w-4" />
            {loading ? 'Starting...' : 'Start Challenge'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            30 Days Challenge
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleViewDetails}
            >
              View Details
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleStopChallenge}
              disabled={loading}
            >
              <StopIcon className="mr-2 h-4 w-4" />
              {loading ? 'Stopping...' : 'Stop'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {renderChallengeStats()}
        {renderCalendar()}
      </CardContent>
    </Card>
  );
};

export default ThirtyDaysChallengeCard; 